import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Heart, X, Sparkles, Lock, Share2, RotateCcw, ArrowRight, Zap, Ruler } from "lucide-react";
import { API, getBreeds } from "@/lib/api";
import { useEntitlement } from "@/lib/entitlement";
import { usePageMeta } from "@/lib/usePageMeta";
import { trackGAEvent } from "@/lib/ga";
import { logWarn } from "@/lib/log";

const SITE_URL = "fetchright.net";
const DECK_SIZE = 5;

const sizeNum = (s) => ({ small: 1, medium: 2, large: 3, giant: 3 }[String(s).toLowerCase()] ?? 2);
const sizeLabel = (s) => ({ 1: "Small", 2: "Medium", 3: "Large" }[sizeNum(s)]);

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Build an "ideal profile" from the liked breeds and describe the visitor's type.
function analyse(likes, allBreeds) {
  if (!likes.length) {
    return {
      persona: "The Hard-to-Please Romantic",
      summary: "Tough crowd! You swiped left on everyone — which means you know exactly what you want. A great dog will have to earn that right swipe.",
      traits: [],
      match: allBreeds[Math.floor(Math.random() * allBreeds.length)],
      wildcard: true,
    };
  }
  const fields = ["energy", "shedding", "grooming", "barking", "trainability", "good_with_kids"];
  const avg = {};
  fields.forEach((f) => { avg[f] = likes.reduce((s, b) => s + (b[f] || 0), 0) / likes.length; });
  avg.size = likes.reduce((s, b) => s + sizeNum(b.size), 0) / likes.length;

  // Nearest breed to the ideal profile across the whole catalogue.
  let match = likes[0], best = Infinity;
  allBreeds.forEach((b) => {
    let d = 0;
    fields.forEach((f) => { d += ((b[f] || 0) - avg[f]) ** 2; });
    d += (sizeNum(b.size) - avg.size) ** 2;
    if (d < best) { best = d; match = b; }
  });

  // Human-readable "type" phrases.
  const phrases = [];
  if (avg.energy >= 4) phrases.push("high-energy adventure buddies");
  else if (avg.energy <= 2.2) phrases.push("laid-back couch companions");
  else phrases.push("easygoing, middle-of-the-road dogs");
  if (avg.shedding >= 3.6 || avg.grooming >= 3.6) phrases.push("fluffy, glamorous coats");
  else if (avg.shedding <= 2.2) phrases.push("low-shedding, tidy types");
  if (avg.size >= 2.6) phrases.push("big, cuddly units");
  else if (avg.size <= 1.5) phrases.push("pint-sized charmers");
  if (avg.good_with_kids >= 4) phrases.push("people-loving softies");
  if (avg.barking <= 2) phrases.push("the strong, silent type");
  else if (avg.barking >= 4) phrases.push("chatty, expressive characters");

  const top = phrases.slice(0, 3);
  const summary = `You go for ${top.slice(0, -1).join(", ")}${top.length > 1 ? " and " : ""}${top[top.length - 1]}. You clearly have a type — and it's a good one.`;

  // Persona title.
  let persona = "The Dog Romantic";
  if (avg.energy >= 4 && avg.size >= 2.5) persona = "The Adventure Seeker";
  else if (avg.energy <= 2.2) persona = "The Cuddle Connoisseur";
  else if (avg.size <= 1.5) persona = "The Lap-Dog Lover";
  else if (avg.grooming >= 3.6 || avg.shedding >= 3.6) persona = "The Glamour Enthusiast";
  else if (avg.good_with_kids >= 4) persona = "The Family-Dog Devotee";

  return { persona, summary, traits: top, match, wildcard: false };
}

export default function SpeedDating() {
  usePageMeta(
    "Dog Speed Dating — swipe to find your type | Fetchright",
    "Swipe like or pass through dogs and discover your type. A fast, playful dog-matching game from Fetchright."
  );
  const { unlocked } = useEntitlement();

  const [allBreeds, setAllBreeds] = useState([]);
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);
  const [likes, setLikes] = useState([]);
  const [step, setStep] = useState("swiping"); // swiping | reveal
  const [result, setResult] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  // Load breeds; restore a pending result if returning from checkout.
  useEffect(() => {
    getBreeds({ limit: 200 }).then((b) => {
      setAllBreeds(b);
      const saved = sessionStorage.getItem("sd_result");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const match = b.find((x) => x.id === parsed.matchId);
          if (match) { setResult({ ...parsed, match }); setStep("reveal"); return; }
        } catch { /* ignore malformed */ }
      }
      setDeck(shuffle(b).slice(0, DECK_SIZE));
    }).catch(() => setAllBreeds([]));
  }, []);

  const current = deck[index];

  const swipe = (liked) => {
    const breed = deck[index];
    const nextLikes = liked ? [...likes, breed] : likes;
    if (liked) setLikes(nextLikes);
    if (index + 1 < deck.length) {
      setIndex(index + 1);
    } else {
      const r = analyse(nextLikes, allBreeds);
      setResult(r);
      setStep("reveal");
      sessionStorage.setItem("sd_result", JSON.stringify({
        persona: r.persona, summary: r.summary, traits: r.traits,
        wildcard: r.wildcard, matchId: r.match.id,
      }));
      trackGAEvent("speed_dating_complete", { persona: r.persona, likes: nextLikes.length });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const replay = () => {
    sessionStorage.removeItem("sd_result");
    setLikes([]); setIndex(0); setResult(null); setStep("swiping");
    setDeck(shuffle(allBreeds).slice(0, DECK_SIZE));
  };

  const unlock = async () => {
    setUnlocking(true);
    try {
      sessionStorage.setItem("sd_pending", "1"); // route back here after payment
      const r = await axios.post(`${API}/payments/checkout`,
        { lookup_key: "unlock_lifetime", origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      logWarn("Speed-dating checkout failed:", e?.message);
      toast.error("Couldn't start checkout — please try again");
      setUnlocking(false);
    }
  };

  const share = async () => {
    const text = result
      ? `Apparently I'm "${result.persona}" 🐶 I played Dog Speed Dating on Fetchright — find your type 👇`
      : "Find your dog type with Dog Speed Dating 🐶";
    const url = `https://${SITE_URL}/speed-dating`;
    try {
      if (navigator.share) await navigator.share({ title: "Dog Speed Dating", text, url });
      else { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Link copied — paste it in your TikTok bio!"); }
      trackGAEvent("speed_dating_share", { method: navigator.share ? "native" : "copy" });
    } catch { /* dismissed */ }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#F2CC8F]/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-[#E07A5F]/20 blur-3xl" />

      <div className="relative max-w-md mx-auto px-6 pt-10 pb-24">
        {step === "swiping" ? (
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
              <Heart size={13} /> Dog Speed Dating
            </span>
            <h1 className="mt-5 font-serif font-black tracking-tight text-accent text-4xl sm:text-5xl leading-[0.95]">
              Swipe your way<br />to your type
            </h1>
            <p className="mt-4 text-card-foreground/70">
              Like or pass on {DECK_SIZE} dogs. We'll tell you what your swipes say about you.
            </p>

            <div className="mt-8 text-sm font-semibold text-muted-foreground">
              {index + 1} of {deck.length || DECK_SIZE}
            </div>

            <div className="relative mt-3 h-[440px]">
              <AnimatePresence mode="wait">
                {current && (
                  <motion.div
                    key={current.id}
                    data-testid="sd-card"
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    onDragEnd={(e, info) => {
                      if (info.offset.x > 110) swipe(true);
                      else if (info.offset.x < -110) swipe(false);
                    }}
                    initial={{ scale: 0.92, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ x: 0, opacity: 0, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 260, damping: 24 }}
                    className="absolute inset-0 rounded-[2rem] overflow-hidden bg-accent text-accent-foreground shadow-[0_20px_60px_rgba(42,54,35,0.35)] cursor-grab active:cursor-grabbing"
                  >
                    <img src={current.image} alt={current.name} className="absolute inset-0 w-full h-full object-cover" draggable="false" />
                    <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6 text-white text-left">
                      <h2 className="font-serif font-black text-3xl">{current.name}</h2>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold">
                          <Zap size={12} /> Energy {current.energy}/5
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold">
                          <Ruler size={12} /> {sizeLabel(current.size)}
                        </span>
                        {current.temperament?.[0] && (
                          <span className="inline-flex items-center rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold">
                            {current.temperament[0]}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-6 flex items-center justify-center gap-6">
              <button
                onClick={() => swipe(false)}
                data-testid="sd-pass-btn"
                aria-label="Pass"
                className="pill-btn w-16 h-16 rounded-full bg-card border-2 border-border grid place-items-center text-muted-foreground hover:border-red-300 hover:text-red-500 hover:-translate-y-0.5"
              >
                <X size={26} strokeWidth={2.5} />
              </button>
              <button
                onClick={() => swipe(true)}
                data-testid="sd-like-btn"
                aria-label="Like"
                className="pill-btn w-20 h-20 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-[0_8px_30px_rgba(224,122,95,0.4)] hover:-translate-y-0.5"
              >
                <Heart size={30} fill="currentColor" />
              </button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Tip: swipe the card, or tap the buttons</p>
          </div>
        ) : result && (
          <div>
            {/* Free "You have a type" summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              data-testid="sd-type-summary"
              className="rounded-[2rem] bg-accent text-accent-foreground p-7 text-center shadow-[0_20px_60px_rgba(42,54,35,0.35)]"
            >
              <div className="text-[#F2CC8F] text-xs font-bold uppercase tracking-[0.2em]">You have a type</div>
              <h1 className="mt-2 font-serif font-black text-3xl leading-tight">{result.persona}</h1>
              <p className="mt-4 text-[15px] leading-relaxed text-accent-foreground/85">{result.summary}</p>
              {result.traits.length > 0 && (
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {result.traits.map((t) => (
                    <span key={t} className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold capitalize">{t}</span>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Perfect match card — locked unless unlocked */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}
              className="mt-6 rounded-[2rem] border border-border bg-card overflow-hidden warm-shadow"
            >
              <div className="relative">
                <img
                  src={result.match.image}
                  alt={unlocked ? result.match.name : "Your match"}
                  className={`w-full aspect-[4/3] object-cover ${unlocked ? "" : "blur-xl scale-110"}`}
                />
                {!unlocked && (
                  <div className="absolute inset-0 bg-accent/50 grid place-items-center">
                    <Lock size={40} className="text-white/90" />
                  </div>
                )}
              </div>

              {unlocked ? (
                <div className="p-6 text-center" data-testid="sd-match-revealed">
                  <div className="text-primary text-xs font-bold uppercase tracking-widest">Your perfect match</div>
                  <h2 className="mt-1 font-serif font-black text-3xl text-accent">{result.match.name}</h2>
                  <p className="mt-2 text-sm text-muted-foreground">{result.match.summary}</p>
                  <Link
                    to={`/breeds/${result.match.id}`}
                    data-testid="sd-view-breed"
                    className="pill-btn mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-bold"
                  >
                    See the full breed profile <ArrowRight size={16} />
                  </Link>
                </div>
              ) : (
                <div className="p-6 text-center" data-testid="sd-match-locked">
                  <div className="text-accent text-xs font-bold uppercase tracking-widest">Your perfect match is…</div>
                  <h2 className="mt-1 font-serif font-black text-2xl text-accent blur-[6px] select-none">Hidden Breed</h2>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Unlock your best-guess match from these swipes — plus every feature on Fetchright, for life.
                  </p>
                  <button
                    onClick={unlock}
                    disabled={unlocking}
                    data-testid="sd-unlock-btn"
                    className="pill-btn mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 text-lg font-bold shadow-[0_8px_30px_rgba(224,122,95,0.35)] disabled:opacity-60"
                  >
                    <Sparkles size={20} /> {unlocking ? "Opening secure checkout…" : "Pay $2 to reveal your match"}
                  </button>
                  <p className="mt-3 text-xs text-muted-foreground">One-time $2 · unlocks everything, forever</p>
                </div>
              )}
            </motion.div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button onClick={share} data-testid="sd-share-btn"
                className="pill-btn inline-flex items-center gap-2 rounded-full border-2 border-accent text-accent px-6 py-3 text-sm font-bold hover:bg-accent hover:text-accent-foreground">
                <Share2 size={16} /> Share my type
              </button>
              <button onClick={replay} data-testid="sd-replay-btn"
                className="pill-btn inline-flex items-center gap-2 rounded-full border-2 border-border text-muted-foreground px-6 py-3 text-sm font-bold hover:border-primary/60">
                <RotateCcw size={16} /> Play again
              </button>
            </div>

            <div className="mt-8 text-center">
              <Link to="/quiz" data-testid="sd-quiz-cta"
                className="inline-flex items-center gap-1.5 font-bold text-primary hover:gap-2.5 transition-all">
                Want your real, accurate match? Take the full quiz <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
